import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-startse-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transform hover:scale-105 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-r from-startse-blue to-startse-blue-600 text-white hover:from-startse-blue-600 hover:to-startse-blue-700 shadow-lg hover:shadow-xl",
        primary: "bg-gradient-to-r from-startse-blue to-startse-blue-600 text-white hover:from-startse-blue-600 hover:to-startse-blue-700 shadow-lg hover:shadow-xl",
        destructive:
          "bg-red-600 text-white hover:bg-red-700 hover:shadow-lg hover:shadow-red-500/25 border border-red-500/20",
        outline:
          "bg-white dark:bg-card border-2 border-gray-200 dark:border-border hover:border-startse-blue-300 dark:hover:border-primary text-gray-700 dark:text-foreground hover:bg-startse-blue-50 dark:hover:bg-muted hover:text-startse-blue-700 dark:hover:text-primary",
        secondary:
          "bg-gray-100 dark:bg-card text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-muted border border-gray-200 dark:border-border",
        ghost: "bg-transparent hover:bg-startse-blue-50 dark:hover:bg-muted text-gray-700 dark:text-foreground hover:text-startse-blue-700 dark:hover:text-primary",
        link: "text-startse-blue underline-offset-4 hover:underline hover:text-startse-blue-700",
        neon: "bg-transparent border-2 border-fiap-magenta text-fiap-magenta hover:bg-fiap-magenta hover:text-white hover:shadow-lg hover:shadow-fiap-magenta/50",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-14 rounded-xl px-8 text-base",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        style={{
          pointerEvents: 'auto',
          cursor: 'pointer',
          ...props.style
        }}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
